"use client";

import { type FC, useState } from "react";
import Link from "next/link";
import { ProductLogo } from "@/components/branding/ProductLogo";
import { usePathname } from "next/navigation";

import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  MobileNavHeader,
  MobileNavMenu,
  MobileNavToggle,
  NavbarButton,
} from "@/components/ui/resizable-navbar";
import { useMarketingNavLinks } from "@/hooks/use-marketing-nav";
import { usePlatformAuth } from "@/hooks/use-platform-auth";
import { trackHeroGetStarted } from "@/lib/analytics/track-hero-cta";
import { shouldUseAppShellForAuthenticated } from "@/lib/platform/chrome-policy";

const Header: FC = () => {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const navItems = useMarketingNavLinks();
  const { isAuthenticated } = usePlatformAuth();

  if (isAuthenticated && shouldUseAppShellForAuthenticated(pathname)) {
    return null;
  }

  return (
    <Navbar className="absolute top-6 text-white dark:text-white">
      <NavBody className="!grid !w-full !max-w-7xl !grid-cols-[1fr_auto_1fr] !items-center gap-4 !px-6">
        <Link
          href="/"
          className="relative z-20 flex items-center space-x-2 px-2 py-1 justify-self-start"
        >
          <ProductLogo
            alt="Inora"
            width={28}
            height={28}
            priority
            loading="eager"
            unoptimized
          />
          <span className="hidden font-medium text-white sm:block">Inora</span>
        </Link>

        <NavItems
          items={navItems}
          className="!relative !inset-auto !flex !flex-none !justify-center !justify-self-center text-white !text-[13px]"
        />

        {!isAuthenticated ? (
          <div className="relative z-20 hidden items-center justify-end gap-2 justify-self-end lg:flex">
            <NavbarButton
              href="/login"
              variant="secondary"
              className="!px-3 !py-1.5"
            >
              Log in
            </NavbarButton>
            <NavbarButton
              href="/signup"
              variant="primary"
              className="!px-3 !py-1.5"
              onClick={() => trackHeroGetStarted("marketing_header_desktop")}
            >
              Sign up
            </NavbarButton>
          </div>
        ) : null}
      </NavBody>

      <MobileNav>
        <MobileNavHeader>
          <Link href="/" className="flex items-center space-x-2 px-2 py-1">
            <ProductLogo
              alt="Inora"
              width={28}
              height={28}
              unoptimized
            />
            <span className="font-medium text-white">Inora</span>
          </Link>
          <MobileNavToggle isOpen={open} onClick={() => setOpen(!open)} />
        </MobileNavHeader>
        <MobileNavMenu isOpen={open} onClose={() => setOpen(false)}>
          {navItems.map((item) =>
            item.isCurrent ? (
              <span
                key={item.name}
                className="w-full border-b border-neutral-100 px-2 py-2 text-lg font-medium text-neutral-500 dark:border-neutral-800"
                aria-current="page"
              >
                {item.name}
              </span>
            ) : (
              <Link
                key={item.name}
                href={item.link}
                onClick={() => setOpen(false)}
                className="w-full border-b border-neutral-100 px-2 py-2 text-lg font-medium text-black dark:border-neutral-800 dark:text-white"
              >
                {item.name}
              </Link>
            ),
          )}
          {!isAuthenticated ? (
            <div className="mt-6 flex w-full flex-col gap-3">
              <NavbarButton
                href="/login"
                variant="secondary"
                className="w-full border border-neutral-200 text-center text-black dark:border-neutral-700 dark:text-white"
                onClick={() => setOpen(false)}
              >
                Log in
              </NavbarButton>
              <NavbarButton
                href="/signup"
                variant="primary"
                className="w-full bg-black text-center text-white dark:bg-white dark:text-black"
                onClick={() => {
                  trackHeroGetStarted("marketing_header_mobile");
                  setOpen(false);
                }}
              >
                Sign up
              </NavbarButton>
            </div>
          ) : null}
        </MobileNavMenu>
      </MobileNav>
    </Navbar>
  );
};

export default Header;
